import "./style.scss";

import React, { FC, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { ReactComponent as IconCheck } from "../../assets/img/icons/icon_Check.svg";
import { ReactComponent as IconChevronDown } from "../../assets/img/icons/icon_carret-Down.svg";
import { ReactComponent as IconChevronUp } from "../../assets/img/icons/icon_carret-Up.svg";
import MoneyDisplay from "../MoneyDisplay";
import { Pricing } from "shared/config/pricings";

interface PriceCardDetailProps {
  plan?: Pricing | null;
  features?: string[] | null;
  isMonth?: boolean;
  isWithinModal?: boolean;
}

const PriceCardDetail: FC<PriceCardDetailProps> = ({
                                                     plan,
                                                     isMonth,
                                                     features,
                                                     isWithinModal = true
                                                   }) => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  // Determine how many features to show initially
  const initialFeaturesCount = 2;
  const initialFeatures = features?.slice(0, initialFeaturesCount);
  const hiddenFeatures = features?.slice(initialFeaturesCount);

  const hasMoreFeatures = features && features.length > initialFeaturesCount;

  const toggleShowAll = () => {
    setShowAllFeatures(!showAllFeatures);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    show: { opacity: 1, y: 0 }
  };

  const hiddenFeaturesVariants = {
    hidden: { opacity: 0, height: 0 },
    show: {
      opacity: 1,
      height: "auto",
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const toggleVariants = {
    initial: { scale: 1 },
    tap: { scale: 0.95 }
  };

  return (
    <div className="PriceCardDetail">
      {isWithinModal && (
        <>
          <MoneyDisplay
            plan={plan}
            isMonth={isMonth}
          />
          <h3>{plan?.title}</h3>
        </>
      )}

      <motion.ul
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        {/* Initial features - always visible */}
        {initialFeatures?.map((feature, index) => (
          <motion.li
            key={index}
            variants={itemVariants}
            className={plan?.featuresRange?.includes(index) || plan?.features ? "within" : "without"}
          >
            <span className="icon-check">
              <IconCheck
                fill="#fff"
                width={16}
                height={16}
              />
            </span>
            <span>{feature}</span>
          </motion.li>
        ))}

        {/* Hidden features - animated when expanded */}
        <AnimatePresence>
          {showAllFeatures && hiddenFeatures && (
            <motion.div
              key="hidden-features"
              variants={hiddenFeaturesVariants}
              initial="hidden"
              animate="show"
              exit="hidden"
              style={{ overflow: "hidden" }}
            >
              {hiddenFeatures.map((feature, index) => (
                <motion.li
                  key={index + initialFeaturesCount}
                  variants={itemVariants}
                  className={plan?.featuresRange?.includes(index + initialFeaturesCount) || plan?.features ? "within" : "without"}
                >
                  <span className="icon-check">
                    <IconCheck
                      fill="#fff"
                      width={16}
                      height={16}
                    />
                  </span>
                  <span>{feature}</span>
                </motion.li>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.ul>

      {hasMoreFeatures && (
        <motion.div
          className="PriceCardDetail__toggle"
          onClick={toggleShowAll}
          variants={toggleVariants}
          initial="initial"
          whileHover={{ scale: 1.05 }}
          whileTap="tap"
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          {showAllFeatures ? (
            <>
              <IconChevronUp width={16} height={16} fill={"#9FC1EF"} />
              <motion.span
                className="PriceCardDetail__toggle-text"
                initial={{ opacity: 0.8 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                Voir moins
              </motion.span>
            </>
          ) : (
            <>
              <motion.span
                className="PriceCardDetail__toggle-text"
                initial={{ opacity: 0.8 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                Voir plus
              </motion.span>
              <IconChevronDown width={16} height={16} fill={"#9FC1EF"} />
            </>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default PriceCardDetail;